import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { UsersCacheService } from '@wilson/account';
import {
  ShiftId,
  TimelineStayGateway,
  UserTimelinesGateway,
} from '@wilson/api/gateway';
import { AuthState } from '@wilson/auth/core';
import {
  Shift,
  Stay,
  StayListItem,
  StaySearchOptions,
  StayStatus,
  StaySuggestionInfo,
  TimelineShift,
  WithPreparedAttributes,
} from '@wilson/interfaces';
import { LocationsStoreService } from '@wilson/locations';
import { getStartDatetime } from '@wilson/non-domain-specific/overlap-helpers';
import { StaysDrawerStateService } from '@wilson/non-domain-specific/stays-form-and-drawer';
import { PipesModule } from '@wilson/pipes';
import {
  SetPreferredSetting,
  ShiftTimelinePreferredSettingsState,
} from '@wilson/preferred-settings/state';
import {
  HighlightSelectedShifts,
  HighlightSelectedStays,
  PinUsersOnTimeline,
  ShiftTimelineDataState,
  ShiftTimelineFilterStateService,
  TimelineSetCurrentDate,
} from '@wilson/shift-timeline/state';
import { DateTimeFormat, getTimeZoneCorrectedDateRange } from '@wilson/utils';
import { add, format, isAfter, isBefore } from 'date-fns';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { combineLatest, firstValueFrom, forkJoin, map, Observable } from 'rxjs';
import { TimelineCommonOperationsService } from '../../../services/timeline-common-operations.service';
import { ShiftTimelineService } from '../../shift-timeline/shift-timeline.service';
import { DrawerStayCardComponent } from './drawer-stay-card/drawer-stay-card.component';

@Component({
  selector: 'wilson-stays-kpi-drawer-list',
  templateUrl: './stays-kpi-drawer-list.component.html',
  styleUrls: ['./stays-kpi-drawer-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    DrawerStayCardComponent,
    NzCollapseModule,
    PipesModule,
    NzSkeletonModule,
    FontAwesomeModule,
  ],
})
export class StaysKpiDrawerListComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly timelineStayGateway = inject(TimelineStayGateway);
  private readonly locationStoreService = inject(LocationsStoreService);
  private readonly usersCacheService = inject(UsersCacheService);
  private readonly userTimelinesGateway = inject(UserTimelinesGateway);
  private readonly staysDrawerStateService = inject(StaysDrawerStateService);
  private readonly shiftTimelineFilterStateService = inject(
    ShiftTimelineFilterStateService,
  );
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly translate = inject(TranslateService);
  private readonly shiftTimelineService = inject(ShiftTimelineService);

  @Output() closeDrawer = new EventEmitter();

  public invalidStays: StayListItem[] = [];
  public necessaryStays: StaySuggestionInfo[] = [];
  public isLoadingInvalidStays = false;
  public isLoadingNecessaryStays = false;
  readonly faChevronUp = faChevronUp;
  readonly faChevronDown = faChevronDown;

  async ngOnInit(): Promise<void> {
    await this.loadStaysDetail();
  }

  async loadStaysDetail(): Promise<void> {
    this.isLoadingInvalidStays = true;
    this.isLoadingNecessaryStays = true;
    this.invalidStays = [];
    this.necessaryStays = [];
    this.cdRef.detectChanges();

    const visibleDateRange = await firstValueFrom(
      this.store.select(ShiftTimelineDataState.visibleDateRange),
    );
    const filteredUsers = await firstValueFrom(
      this.shiftTimelineFilterStateService.filteredUsers$,
    );
    const filteredUserIds = filteredUsers.map((user) => user.id as string);

    if (visibleDateRange.start && visibleDateRange.end) {
      const correctedInterval = getTimeZoneCorrectedDateRange(visibleDateRange);
      this.loadInvalidStays({
        startDatetime: correctedInterval.start.toISOString(),
        endDatetime: correctedInterval.end.toISOString(),
        statuses: [
          StayStatus.Open,
          StayStatus.Requested,
          StayStatus.Confirmed,
          StayStatus.Invoiced,
        ],
        userIds: filteredUserIds,
      });
      this.loadNecessaryStays(
        {
          start: correctedInterval.start,
          end: correctedInterval.end,
        },
        filteredUserIds,
      );
    }
  }

  private async loadInvalidStays(
    payload: Partial<StaySearchOptions>,
  ): Promise<void> {
    try {
      const combinedStays = await firstValueFrom(
        this.timelineStayGateway.getStays(payload).pipe(
          map((stays) => {
            return stays.reduce(
              (acc, stay) => {
                acc[stay.id] = stay;
                return acc;
              },
              {} as Record<string, Stay>,
            );
          }),
        ),
      );

      const stayIds = Object.keys(combinedStays);
      if (stayIds.length) {
        const validatedStays = await firstValueFrom(
          this.timelineStayGateway.validateStays(stayIds),
        );

        const validObservables = Object.keys(validatedStays).reduce(
          (acc, stayId) => {
            const stay = combinedStays[stayId];
            if (stay && !validatedStays[stayId]) {
              const user$ = this.usersCacheService.getUserFromCache(
                stay.userId,
              );
              const location$ = this.locationStoreService.getLocationFromCache(
                stay.locationId,
              );

              acc.push(
                forkJoin([user$, location$]).pipe(
                  map(([user, location]) => ({
                    ...stay,
                    location,
                    user,
                  })),
                ),
              );
            }
            return acc;
          },
          [] as Observable<StayListItem>[],
        );
        if (validObservables.length > 0) {
          this.invalidStays = await firstValueFrom(forkJoin(validObservables));
        } else {
          this.invalidStays = [];
        }

        this.invalidStays.sort((a, b) => {
          const dateA = new Date(a.cancellationDate || a.startDatetime);
          const dateB = new Date(b.cancellationDate || b.startDatetime);
          if (isBefore(dateA, dateB)) {
            return -1;
          } else if (isAfter(dateA, dateB)) {
            return 1;
          } else {
            return 0;
          }
        });
      }
    } catch {
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
    }
    this.isLoadingInvalidStays = false;
    this.cdRef.detectChanges();
  }

  private async loadNecessaryStays(
    dateRange: {
      start: Date;
      end: Date;
    },
    userIds: string[],
  ): Promise<void> {
    try {
      const options =
        await this.shiftTimelineFilterStateService.generateFilterOptionsForAssignedRegion(
          dateRange.start,
          dateRange.end,
          userIds,
        );

      const [combinedShifts, necessaryStaysInRange] = await firstValueFrom(
        combineLatest([
          this.userTimelinesGateway
            .getUserTimelinesWithoutActivities(options)
            .pipe(
              map((userTimelines) => {
                const shiftsRecord: Record<ShiftId, TimelineShift> = {};
                userTimelines.forEach((userTimeline) => {
                  userTimeline.shifts.forEach((shift) => {
                    shiftsRecord[shift.id as ShiftId] = shift;
                  });
                });
                return shiftsRecord;
              }),
            ),
          this.timelineStayGateway.fetchNecessaryStaysInRange({
            dateRange,
            userIds,
          }),
        ]),
      );

      const validObservables = Object.keys(necessaryStaysInRange).reduce(
        (acc, shiftId) => {
          const shift = combinedShifts[shiftId];
          if (necessaryStaysInRange[shiftId] && shift) {
            acc.push(
              this.usersCacheService
                .getUserFromCache(shift.userId as string)
                .pipe(
                  map((user) => ({
                    locationId: shift.endLocationId,
                    userId: user?.id as string,
                    shiftId: shift.id,
                    shift: shift,
                    date: shift.reportedEndDatetime || shift.endDatetime,
                    locationName: `${shift.endLocation.locationCode}${
                      shift.endLocation.locationCode ? ' - ' : ''
                    }${shift.endLocation.name}`,
                    userName: `${user?.lastName}, ${user?.firstName}`,
                    cancellationDate: null,
                  })),
                ),
            );
          }
          return acc;
        },
        [] as Observable<StaySuggestionInfo>[],
      );
      if (validObservables.length > 0) {
        this.necessaryStays = await firstValueFrom(forkJoin(validObservables));
      } else {
        this.necessaryStays = [];
      }

      this.necessaryStays.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        if (isBefore(dateA, dateB)) {
          return -1;
        } else if (isAfter(dateA, dateB)) {
          return 1;
        } else {
          return 0;
        }
      });
    } catch {
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
    }
    this.isLoadingNecessaryStays = false;
    this.cdRef.detectChanges();
  }

  openCreateStaysDrawer(staySuggestionInfo: StaySuggestionInfo): void {
    this.staysDrawerStateService.setStaySuggestionInfo(staySuggestionInfo);
    this.staysDrawerStateService.toggleCreateStaysDrawer(true);
    const subscription =
      this.staysDrawerStateService.isCreateStaysDrawerVisible$.subscribe(
        async (isVisible) => {
          if (!isVisible) {
            subscription.unsubscribe();
            await this.loadStaysDetail();
          }
        },
      );
  }

  openEditStaysDrawer(stayList: StayListItem): void {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { user, location, ...stay } = stayList;
    this.staysDrawerStateService.setStayToEdit(stay);
    this.staysDrawerStateService.setEditStaysDrawerVisibility(true);
    const subscription =
      this.staysDrawerStateService.isEditStaysDrawerVisible$.subscribe(
        async (isVisible) => {
          if (!isVisible) {
            subscription.unsubscribe();
            await this.loadStaysDetail();
          }
        },
      );
  }

  async navigateToTimeline(
    stayId: string | null,
    shift: (Shift & WithPreparedAttributes & { id: string }) | null,
    userId: string,
    date: string,
  ): Promise<void> {
    this.closeDrawer.emit();
    const numberOfDaysToLoad =
      this.timelineCommonOperationsService.calculateNumberOfDaysToLoad();
    const determinedStartDate = shift ? getStartDatetime(shift).date : date;
    const startDate = new Date(determinedStartDate);
    const endDate = add(startDate, {
      days: numberOfDaysToLoad,
    });
    const formattedDate = format(startDate, DateTimeFormat.DatabaseDateFormat);

    this.store.dispatch(new TimelineSetCurrentDate(formattedDate));
    this.timelineCommonOperationsService.setTimelineDate(startDate, endDate);
    if (stayId) {
      this.store.dispatch(new HighlightSelectedStays([stayId]));
    }
    if (shift) {
      this.store.dispatch(new HighlightSelectedShifts([shift.id]));
    }
    this.store.dispatch(new PinUsersOnTimeline([userId]));
    this.shiftTimelineService.forceGanttChartRerender();
    const loggedInUserId = this.store.selectSnapshot(AuthState.userId);
    if (loggedInUserId) {
      const preferredSetting = this.store.selectSnapshot(
        ShiftTimelinePreferredSettingsState.getPreferredSetting(loggedInUserId),
      );
      this.store.dispatch([
        new SetPreferredSetting(loggedInUserId, {
          staffLayout: preferredSetting.staffLayout,
          assignmentLayout: preferredSetting.assignmentLayout,
          mainKPIs: preferredSetting.mainKPIs,
          sortBy: preferredSetting.sortBy,
          sortDirection: preferredSetting.sortDirection,
          threshold: preferredSetting.threshold,
          shiftColorOption: preferredSetting.shiftColorOption,
        }),
      ]);

      setTimeout(() => {
        this.store.dispatch([
          new HighlightSelectedStays([]),
          new HighlightSelectedShifts([]),
          new PinUsersOnTimeline([]),
        ]);
      }, 4000);
    }
  }
}
